import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, FileText, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { UploadedFile } from '../types';

interface FileUploadStepProps {
  uploadedFile: UploadedFile | null;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  onNext: () => void;
  uploadProgress: number;
  isUploading: boolean;
}

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  uploadedFile,
  onFileSelect,
  onFileRemove,
  onNext,
  uploadProgress,
  isUploading,
}) => {
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // Accept any file but guide users visually in the UI copy
    accept: undefined,
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024, // Align with bucket 100MB limit
    disabled: isUploading || !!uploadedFile,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Your Sequence File</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!uploadedFile ? (
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200
              ${isDragActive 
                ? 'border-primary bg-primary/5' 
                : 'border-gray-300 hover:border-gray-400'
              }
              ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-2">
              {isDragActive
                ? 'Drop your sequence file here'
                : 'Drag & drop your sequence file here'}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              or click to browse your files
            </p>
            <p className="text-xs text-gray-500">
              Suggested formats: .zip, .fseq, .lms, .seq, .xml, .piz, .loredit (Max 100MB)
            </p>
          </div>
        ) : (
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-gray-600">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
              </div>
              {!isUploading && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onFileRemove}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            {isUploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Uploading...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {uploadedFile.uploadedPath && !isUploading && (
              <div className="mt-3 flex items-center text-sm text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                File uploaded successfully
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={onNext}
            disabled={!uploadedFile || isUploading}
          >
            Next: Basic Information
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FileUploadStep;